import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import { getCloudflareUploadUrls } from 'api/apiCloudflare';
import { uploadFileWithProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import { runAiJobWithPolling } from 'utils/helpers/ai/runAiJobWithPolling';
import type { TemplateDocAiResultType } from '@econtractor/types';

type TemplateDocAiPhase = 'idle' | 'uploading' | 'processing';

interface UseTemplateDocAiParams {
    onParsed: (result: TemplateDocAiResultType) => void;
}

const ACCEPTED_EXTENSIONS = ['pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg', 'webp', 'gif'];

const isAcceptedFile = (file: File): boolean => {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    return ACCEPTED_EXTENSIONS.includes(ext);
};

const useTemplateDocAi = ({ onParsed }: UseTemplateDocAiParams) => {
    const { t } = useTranslation('TemplateValueKeys');
    const { showToast } = useToast();
    const refInputFile = useRef<HTMLInputElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const onParsedRef = useRef(onParsed);
    onParsedRef.current = onParsed;
    const [isBusy, setIsBusy] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number | boolean>(false);
    const [phase, setPhase] = useState<TemplateDocAiPhase>('idle');

    const processingLabel =
        phase === 'uploading' ? t('aiDoc.loadingUpload') : t('aiDoc.loadingProcess');

    const resetInput = () => {
        if (refInputFile.current) refInputFile.current.value = '';
    };

    const handleOpenClick = () => {
        if (isBusy) return;
        refInputFile.current?.click();
    };

    const handleCancel = () => {
        if (phase !== 'processing') return;
        abortControllerRef.current?.abort();
    };

    const handleChangeFile = async () => {
        const file = refInputFile.current?.files?.[0];
        if (!file) return;
        if (!isAcceptedFile(file)) {
            showToast(t('aiDoc.messParseError'), 'warning');
            resetInput();
            return;
        }

        setIsBusy(true);
        setPhase('uploading');
        setUploadProgress(1);

        try {
            const uploadConfigs = await getCloudflareUploadUrls(
                [
                    {
                        fileName: file.name,
                        contentType: file.type || 'application/octet-stream',
                        fileSize: file.size,
                    },
                ],
                {},
                { keyPrefix: 'aitemp' },
            );
            const uploadConfig = uploadConfigs?.[0];
            if (!uploadConfig) throw new Error('cloud_upload_urls_missing');

            const uploaded = await uploadFileWithProgress(file, uploadConfig, setUploadProgress);
            if (!uploaded.success || !uploaded.config?.publicUrl) {
                throw new Error('cloud_upload_failed');
            }

            setUploadProgress(100);
            setPhase('processing');
            abortControllerRef.current = new AbortController();
            const job = await runAiJobWithPolling<{ fileUrl: string; fileName: string }, TemplateDocAiResultType>({
                requestType: 'templates.document.parse-file',
                payload: {
                    fileUrl: uploaded.config.publicUrl,
                    fileName: file.name,
                },
                pollIntervalMs: 2000,
                timeoutMs: 180000,
                signal: abortControllerRef.current.signal,
            });

            if (job.error || job.status !== 'done' || !job.result) {
                throw new Error(job.error || 'ai_job_failed');
            }

            const result = job.result;
            const hasHtml = Boolean(result.proposalTitle || result.proposalIntro || result.proposalOutro);
            if (!hasHtml) {
                showToast(t('aiDoc.messEmpty'), 'warning');
                return;
            }

            onParsedRef.current(result);
        } catch (error) {
            const message = (error as Error)?.message || '';
            if (message === 'ai_job_aborted' || message === 'canceled') {
                showToast(t('aiDoc.messCanceled'), 'warning');
            } else {
                console.error(error);
                showToast(t('aiDoc.messParseError'), 'danger');
            }
        } finally {
            abortControllerRef.current = null;
            setIsBusy(false);
            setPhase('idle');
            setUploadProgress(false);
            resetInput();
        }
    };

    return {
        isBusy,
        phase,
        uploadProgress,
        processingLabel,
        refInputFile,
        handleOpenClick,
        handleCancel,
        handleChangeFile,
    };
};

export default useTemplateDocAi;
